import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExerciseScores,
  SeriesScores,
  StudentSeriesScores,
} from '../../../../config/api/models/Score';
import { Icons, SvgIcon, TooltipTitleText } from '../../../shared/components';
import { useStudentsByGroup } from '../../../shared/hooks';
import { styled } from '@material-ui/core';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

type Props = {
  data: Record<string, StudentSeriesScores>;
  seriesId: string;
};

const Wrapper = styled('div')({
  overflow: 'hidden',
});

const StudentName = styled('td')({
  padding: '0 2rem 0 0',
});

const StudentData = styled('td')({
  padding: '0 0.5rem',
  textAlign: 'right',
});

const getAverageScores = (scores: Record<string, SeriesScores>, seriesId: string): string => {
  if (!scores) return '';
  if (!seriesId) return '';
  if (!scores[seriesId]) return '';

  const seriesScores: Record<string, ExerciseScores> = scores[seriesId];
  const keys: string[] = Object.keys(seriesScores);

  let totalScores = 0;
  let totalMaxScores = 0;

  for (let i = 0; i < keys.length; i++) {
    totalScores += seriesScores[keys[i]].score;
    totalMaxScores += seriesScores[keys[i]].maxScore;
  }

  if (totalMaxScores === 0) return '0%';

  const score = totalMaxScores > 0 ? (totalScores / totalMaxScores) * 100 : 0;
  const scorePercentage = score < 1 ? Math.ceil(score) : Math.floor(score);

  return `${scorePercentage}%`;
};

const getAverageProgress = (scores: Record<string, SeriesScores>, seriesId: string): string => {
  if (!scores) return '';
  if (!seriesId) return '';
  if (!scores[seriesId]) return '';

  const seriesScores: Record<string, ExerciseScores> = scores[seriesId];
  const keys: string[] = Object.keys(seriesScores);

  let totalProgress = 0;

  for (let i = 0; i < keys.length; i++) {
    totalProgress += seriesScores[keys[i]].isAnswered ? 1 : 0;
  }

  return `${totalProgress}/${keys.length}`;
};

const TeacherSeriesTooltip: FC<Props> = ({ data, seriesId }) => {
  const { t } = useTranslation();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const { data: studentsData } = useStudentsByGroup(
    selectedGroup || '',
    selectedGroup !== undefined && selectedGroup !== ''
  );

  return studentsData ? (
    <>
      <Wrapper>
        <table>
          <thead>
            <tr>
              <StudentName>
                <TooltipTitleText>{t('progressAndScores.series.student')}</TooltipTitleText>
              </StudentName>
              <StudentData>
                <SvgIcon icon={Icons.PROGRESS} />
              </StudentData>
              <StudentData>
                <SvgIcon icon={Icons.SCORES} />
              </StudentData>
            </tr>
          </thead>
          <tbody>
            {studentsData.data.map((student) => (
              <tr key={student.id}>
                <StudentName>{`${student.fullName}`}</StudentName>
                <StudentData>
                  {data[student.id] && data[student.id].scores && seriesId
                    ? `${getAverageProgress(data[student.id].scores, seriesId)}`
                    : ''}
                </StudentData>
                <StudentData>
                  {data[student.id] && data[student.id].scores && seriesId
                    ? `${getAverageScores(data[student.id].scores, seriesId)}`
                    : ''}
                </StudentData>
              </tr>
            ))}
          </tbody>
        </table>
      </Wrapper>
    </>
  ) : null;
};

export default TeacherSeriesTooltip;
